import React, { PropsWithChildren } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { Icon, LoadingDots } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderRailCardStatusIcon = (props: ReorderRailCardStatusIconProps) => {
  const { status, disableAnimation } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const statusBadgeStyles = [
    styles.statusBadge,
    status === 'success' ? styles.statusBadgeSuccess : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={statusBadgeStyles}>
      {status === 'idle' ? (
        <AnimationWrapper disableAnimation={disableAnimation}>
          <Icon
            name="IconPlus2"
            color={theme.colors.DARK_KALE}
            width={ICON_SIZE}
            height={ICON_SIZE}
          />
        </AnimationWrapper>
      ) : null}

      {status === 'loading' ? (
        <AnimationWrapper disableAnimation={disableAnimation}>
          <LoadingDots color={theme.colors.DARK_KALE} />
        </AnimationWrapper>
      ) : null}

      {status === 'success' ? (
        <AnimationWrapper disableAnimation={disableAnimation}>
          <Icon name="IconCheck" color={theme.colors.WHITE} width={ICON_SIZE} height={ICON_SIZE} />
        </AnimationWrapper>
      ) : null}
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const AnimationWrapper = (props: AnimationWrapperProps) => {
  const { children, disableAnimation } = props;

  if (disableAnimation) {
    return <View>{children}</View>;
  }

  return (
    <Animated.View entering={ENTERING} exiting={EXITING}>
      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZE = 22;
const STATUS_BADGE_SIZE = 32;

const ENTERING = FadeIn.duration(theme.transitions.base);
const EXITING = FadeOut.duration(theme.transitions.base);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  statusBadge: {
    position: 'absolute',
    right: theme.spacing['2'],
    bottom: theme.spacing['2'],
    width: STATUS_BADGE_SIZE,
    height: STATUS_BADGE_SIZE,
    borderRadius: STATUS_BADGE_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.WHITE,
  },
  statusBadgeSuccess: {
    backgroundColor: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderRailCardStatus = 'idle' | 'loading' | 'success';

type ReorderRailCardStatusIconProps = {
  status: ReorderRailCardStatus;
  disableAnimation?: boolean;
};

type AnimationWrapperProps = PropsWithChildren &
  Pick<ReorderRailCardStatusIconProps, 'disableAnimation'>;
